import { useMemo } from "react";
import "./VoiceModeVisual.css";
import orbieeVoiceLogo from "../../images/OrbieeLogoOnly.png";

const CX = 100;
const CY = 100;
const INNER_R = 50;
const LINE_COUNT = 128;

const LAYERS = [
  { phase: 0.2, amp: 1, base: 90, className: "voice-mode-visual__burst-layer--a" },
  { phase: 1.4, amp: 0.92, base: 86, className: "voice-mode-visual__burst-layer--b" },
  { phase: 2.5, amp: 0.85, base: 82, className: "voice-mode-visual__burst-layer--c" },
];

/**
 * Resolve visual mode for the voice visual.
 * Priority: muted → idle; recording → listening; loading → thinking; speaking → speaking; else idle.
 */
export function resolveVoiceVisualMode({
  isMicMuted = false,
  isRecording = false,
  isLoading = false,
  isSpeaking = false,
}) {
  if (isMicMuted) return "idle";
  if (isRecording) return "listening";
  if (isLoading) return "thinking";
  if (isSpeaking) return "speaking";
  return "idle";
}

function blobRadius(angle, phase, base, amp) {
  const lobes =
    0.16 * Math.sin(5 * angle + phase) +
    0.09 * Math.sin(7 * angle + phase * 1.35) +
    0.05 * Math.sin(3 * angle + phase * 0.6);
  return base * (0.82 + lobes * amp);
}

function buildBurstPath(phase, base, amp) {
  const parts = [];
  for (let i = 0; i < LINE_COUNT; i += 1) {
    const angle = (i / LINE_COUNT) * Math.PI * 2;
    const outer = blobRadius(angle, phase, base, amp);
    const x0 = CX + Math.cos(angle) * INNER_R;
    const y0 = CY + Math.sin(angle) * INNER_R;
    const x1 = CX + Math.cos(angle) * outer;
    const y1 = CY + Math.sin(angle) * outer;
    parts.push(`M${x0.toFixed(2)} ${y0.toFixed(2)}L${x1.toFixed(2)} ${y1.toFixed(2)}`);
  }
  return parts.join("");
}

/**
 * Orbiee logo with blue radiating-line waves around it.
 * Avatar stays mounted underneath. Does not touch recording, chat, or websocket.
 */
export default function VoiceModeVisual({
  audioLevel = 0,
  isRecording = false,
  isLoading = false,
  isMicMuted = false,
  isSpeaking = false,
}) {
  const mode = resolveVoiceVisualMode({
    isMicMuted,
    isRecording,
    isLoading,
    isSpeaking,
  });

  const levelBoost = isRecording
    ? Math.min((Number(audioLevel) || 0) * 14, 1)
    : mode === "speaking"
      ? 0.75
      : mode === "thinking"
        ? 0.45
        : 0.22;

  const layerPaths = useMemo(
    () =>
      LAYERS.map((layer) => ({
        ...layer,
        d: buildBurstPath(layer.phase, layer.base, layer.amp),
      })),
    []
  );

  return (
    <div
      className="voice-mode-visual"
      data-mode={mode}
      style={{ "--voice-level": levelBoost }}
      aria-hidden="true"
    >
      <div className="voice-mode-visual__stage">
        <svg
          className="voice-mode-visual__burst"
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
        >
          {layerPaths.map((layer) => (
            <path
              key={layer.className}
              className={`voice-mode-visual__burst-layer ${layer.className}`}
              d={layer.d}
              fill="none"
              strokeLinecap="round"
            />
          ))}
        </svg>

        <div className="voice-mode-visual__hub">
          <div className="voice-mode-visual__hub-glow" />
          <div className="voice-mode-visual__ring voice-mode-visual__ring--outer" />
          <div className="voice-mode-visual__ring voice-mode-visual__ring--inner" />
          <img
            className="voice-mode-visual__logo"
            src={orbieeVoiceLogo}
            alt=""
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
}
